import { FormInstance, Typography } from 'antd';
import { FC, memo, useEffect, useMemo, useState } from 'react';
import cn from 'classnames';

import { Filter } from 'componets/ui';
import { Filters } from 'componets/ui/Filter/Filters';
import { FiltersForm } from 'componets/ui/Filter/FiltersForm';
import { FilterItem } from 'componets/ui/Filter/FilterItem';

import { useAppSelector, useTranslate } from 'hooks';
import { FieldData, IFilter, IFilterValues } from 'models';
import { selectGlobals } from 'selectors';
import Utils from 'utils/utils';
import i18n from 'utils/i18n';

import { ReactComponent as DownloadIcon } from 'assets/icons/download.svg';

import s from './index.module.scss';

const MARKET_OPTIONS = [
	{ value: '0', label: i18n.t('primaryMarketText') },
	{ value: '1', label: i18n.t('secondaryMarketTitleText') },
];

export const MyInvestmentsFilters: FC<{
	onDownload: () => void;
	fields: FieldData[];
	setFields: (changedFields: FieldData[], allFields?: FieldData[]) => void;
	form: FormInstance;
	initialValues: IFilterValues;
	values: IFilterValues;
	length: number;
}> = memo(({ onDownload, fields, setFields, form, initialValues, values, length }) => {
	const t = useTranslate([
		'downloadText',
		'projectIdFilterText',
		'companyNameText',
		'projectTypeText',
		'projectNameText',
		'interestRateText',
		'investedAmountText',
		'marketText',
		'dateOfInvestmentText',
		'overdueInterestText',
		'lateLoansText',
	]);

	const [filters, setFilters] = useState([] as IFilter[]);
	const [isOpen, setIsOpen] = useState(false);

	const onResetFields = () => {
		form.setFieldsValue({
			type: [],
			loanId: '',
			projectTitle: '',
			originatorTitle: '',
			'interestRate[from]': undefined,
			'interestRate[to]': undefined,
			'investedAmount[from]': undefined,
			'investedAmount[to]': undefined,
			isPurchased: '',
			minDateOfPurchase: undefined,
			maxDateOfPurchase: undefined,
			overdueInterestPayments: undefined,
			lateLoans: undefined,
		});
	};

	const globals = useAppSelector(selectGlobals);

	const typeOptions = useMemo(
		() =>
			globals.loanTypes?.map((item) => ({
				value: item.type,
				label: Utils.formatProjectType(item.type),
			})) || [],
		[globals.loanTypes],
	);

	useEffect(() => {
		let data: IFilter[] = [];

		if (values) {
			data = Utils.transformFilters(values, globals.countries);
		}

		setFilters(data);
	}, [globals.countries, values]);

	const onClose = () => setIsOpen(false);

	return (
		<div className={s.filters}>
			<div className={s.filtersRow}>
				<div
					role="button"
					className={cn('white-btn', { 'white-btn--opacity': length < 1 })}
					onClick={onDownload}
				>
					<DownloadIcon />
					<Typography.Text className={s.downloadText}>{t.downloadText}</Typography.Text>
				</div>
				<Filter
					onResetFields={onResetFields}
					isOpen={isOpen}
					onOpen={() => setIsOpen(true)}
					onClose={onClose}
				>
					<FiltersForm
						filters={filters}
						onFinish={onClose}
						setFields={setFields}
						form={form}
						initialValues={initialValues}
						fields={fields}
					>
						<FilterItem name="loanId" title={t.projectIdFilterText} isField />

						<FilterItem name="projectTitle" title={t.projectNameText} isField />

						<FilterItem name="originatorTitle" title={t.companyNameText} isField />

						<FilterItem name="date" title={t.dateOfInvestmentText} isDate />

						<FilterItem name="type" title={t.projectTypeText} isCheckbox options={typeOptions} />

						<FilterItem name="interestRate" title={t.interestRateText} isRange />

						<FilterItem name="investedAmount" title={t.investedAmountText} isRange mark={'€'} />

						<FilterItem name="isPurchased" title={t.marketText} isRadio options={MARKET_OPTIONS} />

						<FilterItem
							name="overdueInterestPayments"
							title={t.overdueInterestText}
							isSwitch
							checked={!!Number(values?.overdueInterestPayments)}
							onClick={(value) => {
								setFields([
									{
										name: ['overdueInterestPayments'],
										value: value ? Number(value).toString() : undefined,
									},
								]);
							}}
						/>
						<FilterItem
							name="lateLoans"
							title={t.lateLoansText}
							isSwitch
							checked={!!Number(values?.lateLoans)}
							onClick={(value) => {
								setFields([
									{
										name: ['lateLoans'],
										value: value ? Number(value).toString() : undefined,
									},
								]);
							}}
						/>
					</FiltersForm>
				</Filter>
			</div>

			{filters.length > 0 && (
				<>
					<div className={s.line} />
					<Filters filters={filters} onReset={onResetFields} form={form} values={values} />
				</>
			)}
		</div>
	);
});
